import { CheckCircle2, ListChecks } from 'lucide-react';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { EmptyState } from '../ui/EmptyState';
import { Pill } from '../ui/Pill';
import type { OutstandingItem, StationProgress } from './meTypes';

/**
 * Physical kit the inductee still needs to collect from a station —
 * derived from their role's kit template minus what's already been
 * issued. Separate from "Your tasks" which is action-work.
 *
 * Render rules:
 *   - Hidden entirely when the role's kit template lists nothing
 *     (e.g. no template seeded for this position). Avoids a useless
 *     "All caught up" pat-on-the-back for people who were never
 *     going to have collection items.
 *   - Shown with the cheery empty state when the template DOES have
 *     items but they've all been issued.
 *   - Renders the list otherwise.
 */
export function KitToCollectCard({
  outstanding,
  stationProgress,
  preview,
}: {
  outstanding: OutstandingItem[];
  stationProgress: StationProgress[];
  /**
   * True before the day: the list is a preview of what the inductee will
   * be handed, not a checklist for them to action, so it collapses to a
   * one-line summary to avoid reading as a to-do. On the day it opens up
   * as a live list that shrinks as stations issue each item.
   */
  preview: boolean;
}) {
  const hasTemplateItems = stationProgress.some((s) => s.expected > 0);
  if (!hasTemplateItems) return null;

  const list = (
    <ul className="divide-y divide-divider/10">
      {outstanding.map((it, i) => (
        <li
          key={`${it.station}-${it.name}-${i}`}
          className="flex flex-wrap items-baseline gap-2 px-5 py-2.5 text-sm"
        >
          <Pill tone="accent" size="sm">
            {it.station}
          </Pill>
          {it.category ? (
            <Pill tone="warning" size="sm">
              {it.category}
            </Pill>
          ) : null}
          <span className="font-medium text-fg">{it.name}</span>
          {it.quantity > 1 ? (
            <span className="text-fg-muted">× {it.quantity}</span>
          ) : null}
          {it.size_required ? (
            <Pill tone="warning" size="sm">size required</Pill>
          ) : null}
        </li>
      ))}
    </ul>
  );

  // Before the day, with items still to collect: minimise to a preview so
  // it doesn't look like work the inductee has to do now. The list is
  // flush (no body padding) so it matches the on-the-day card body.
  if (preview && outstanding.length > 0) {
    const count = outstanding.length;
    return (
      <CollapsibleSection
        icon={<ListChecks className="h-4 w-4 text-accent" />}
        title="Kit to collect"
        description="A preview of what you'll be given on your first day. Nothing to collect yet."
        trailing={`${count} item${count === 1 ? '' : 's'}`}
        bodyClassName=""
      >
        {list}
      </CollapsibleSection>
    );
  }

  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <div className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <ListChecks className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-fg">Kit to collect</h2>
      </div>
      {outstanding.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="h-6 w-6" />}
          title="All collected"
          description="Every item on your role's kit list has been issued."
        />
      ) : (
        list
      )}
    </section>
  );
}
